import { useState } from 'react';
import { useAuth } from '../../hooks/useAuth.jsx';
import { apiClient, parseApiError } from '../../services/http.js';
import { useModalA11y } from '../../hooks/useModalA11y.js';

/**
 * Onboarding curto e opcional (P18). Cada pergunta é de escolha fechada (não texto
 * livre) para minimizar dados coletados; nenhuma delas envolve atributo sensível.
 * As respostas só ajustam tom/frequência da experiência — nunca são usadas para
 * publicidade nem compartilhadas com terceiros.
 */

const QUESTIONS = [
  {
    key: 'objetivo_principal',
    label: 'Qual é o seu objetivo principal aqui?',
    purpose: 'Usamos isso apenas para priorizar os recursos que sugerimos a você primeiro.',
    options: [
      { value: 'crescer_na_fe', label: 'Crescer na fé' },
      { value: 'duvidas_doutrinarias', label: 'Tirar dúvidas doutrinárias' },
      { value: 'oracao_diaria', label: 'Manter uma oração diária' },
      { value: 'preparacao_sacramento', label: 'Me preparar para um sacramento' },
      { value: 'outro', label: 'Outro' },
    ],
  },
  {
    key: 'nivel_familiaridade',
    label: 'Qual seu nível de familiaridade com a fé católica?',
    purpose: 'Usamos isso para calibrar o quanto explicamos termos e conceitos nas respostas.',
    options: [
      { value: 'iniciante', label: 'Iniciante' },
      { value: 'intermediario', label: 'Intermediário' },
      { value: 'avancado', label: 'Avançado' },
    ],
  },
  {
    key: 'tempo_diario',
    label: 'Quanto tempo por dia você gostaria de dedicar?',
    purpose: 'Usamos isso para sugerir conteúdos e trilhas do tamanho certo para sua rotina.',
    options: [
      { value: '5min', label: '5 minutos' },
      { value: '15min', label: '15 minutos' },
      { value: '30min', label: '30 minutos' },
      { value: 'mais_30min', label: 'Mais de 30 minutos' },
    ],
  },
  {
    key: 'profundidade_resposta',
    label: 'Como você prefere as respostas?',
    purpose: 'Usamos isso para ajustar o tamanho e o tom das respostas do chat.',
    options: [
      { value: 'direta', label: 'Curtas e diretas' },
      { value: 'aprofundada', label: 'Detalhadas e aprofundadas' },
    ],
  },
  {
    key: 'lembretes',
    label: 'Gostaria de receber lembretes?',
    purpose: 'Usamos isso apenas para decidir se e com que frequência enviamos lembretes por e-mail. Você pode mudar isso quando quiser.',
    options: [
      { value: 'diario', label: 'Sim, diariamente' },
      { value: 'semanal', label: 'Sim, semanalmente' },
      { value: 'nao', label: 'Não, obrigado' },
    ],
  },
];

export default function OnboardingModal({ mode = 'forced', initialPreferences = null, onClose }) {
  const { refreshSession } = useAuth();
  const [answers, setAnswers] = useState(initialPreferences || {});
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const select = (key, value) => setAnswers((prev) => ({ ...prev, [key]: value }));

  const submit = async (skipped) => {
    setLoading(true);
    setError(null);
    try {
      const { res, data } = await apiClient.saveOnboarding({ skipped, ...(skipped ? {} : answers) });
      if (!res.ok) {
        throw new Error(parseApiError(data, 'Erro ao salvar. Tente novamente.'));
      }
      await refreshSession();
      if (onClose) onClose();
    } catch (err) {
      setError(err.message || 'Falha de rede ao se conectar com o servidor.');
    } finally {
      setLoading(false);
    }
  };
  const modalA11y = useModalA11y(true, mode !== 'forced' ? onClose : null, 'onboarding-title');

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/70 backdrop-blur-sm animate-fade-in">
      <div {...modalA11y} className="bg-surface-container-high border border-outline-variant/30 max-w-lg w-full rounded-2xl p-6 md:p-8 shadow-2xl flex flex-col space-y-5 animate-scale-up text-on-surface max-h-[90vh] overflow-y-auto">
        <div className="text-center space-y-2">
          <h2 id="onboarding-title" className="font-serif text-2xl font-bold tracking-tight leading-snug">
            Vamos personalizar sua experiência
          </h2>
          <p className="text-sm font-light text-on-surface-variant leading-relaxed">
            Leva menos de um minuto. Você pode pular ou alterar suas respostas depois, em Configurações.
          </p>
        </div>

        {QUESTIONS.map((q) => (
          <div key={q.key} className="space-y-1.5">
            <p className="text-sm font-semibold">{q.label}</p>
            <div className="flex flex-wrap gap-2">
              {q.options.map((opt) => (
                <button
                  key={opt.value}
                  type="button"
                  disabled={loading}
                  onClick={() => select(q.key, opt.value)}
                  className={`px-3 py-1.5 text-xs rounded-full border transition-colors disabled:opacity-50 ${
                    answers[q.key] === opt.value
                      ? 'bg-primary text-on-primary border-primary'
                      : 'border-outline-variant text-on-surface-variant hover:text-on-surface hover:bg-primary/5'
                  }`}
                >
                  {opt.label}
                </button>
              ))}
            </div>
            <p className="text-[11px] text-on-surface-variant/70">{q.purpose}</p>
          </div>
        ))}

        {error && (
          <div className="p-3 bg-error/10 border border-error/20 text-error rounded-xl text-xs flex items-start gap-2 animate-shake">
            <span className="material-symbols-outlined text-sm mt-0.5">error</span>
            <span className="font-medium">{error}</span>
          </div>
        )}

        <div className="flex gap-3">
          <button
            onClick={() => submit(false)}
            disabled={loading}
            className="flex-1 py-3 bg-primary text-on-primary font-bold rounded-xl shadow-lg hover:opacity-95 active:scale-[0.98] disabled:opacity-40 transition-all"
          >
            {loading ? 'Salvando...' : 'Salvar preferências'}
          </button>
          <button
            onClick={() => (mode === 'edit' && onClose ? onClose() : submit(true))}
            disabled={loading}
            className="px-4 py-3 text-sm text-on-surface-variant hover:text-on-surface transition-colors disabled:opacity-50"
          >
            {mode === 'edit' ? 'Cancelar' : 'Pular por agora'}
          </button>
        </div>
      </div>
    </div>
  );
}
